import S from 's-js';
import { lift } from 's-array';
import { IAjax } from 'lib/Ajax';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import { tzmomentMod } from 'lib/tzmoment';
import { TZUtils } from 'lib/tzutils';
import { ValidationWatcher } from 'lib/validationWatcher';

import { Entrypoints } from 'core/app/entrypoints';

import { PCCache } from 'data/PCCache';

import { Berth } from 'data/models/Berth';
import { BerthReservation } from 'data/models/BerthReservation';
import { DataCacheUpdateEntities } from 'data/models/DataCacheUpdate';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { VesselSide } from 'server/enums/BerthReservation';

import { agentRestrictionErrors } from 'common/services/agentRestrictionsErrorParser';

import { Typeahead } from 'portcall/typeahead/model';

import {
    DEFAULT_NEW_SHIFT_DURATION_MINS,
    SAME_PIERGROUP_TRANSIT_MINS,
    DIFFERENT_PIERGROUP_TRANSIT_MINS
} from 'common/services/scheduleConstants';

export interface AddShiftPopupParams {
    previousPoint : () => ItineraryPoint | null;
    onCancel : () => void;
    onSuccess : (entities : DataCacheUpdateEntities) => void;
}

export function AddShiftPopup(
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    tzutils : TZUtils,
    { previousPoint, onCancel, onSuccess } : AddShiftPopupParams
) {
    const
        newPoint = new ItineraryPoint(),
        newReservation = new BerthReservation({
            Id: 0,
            SideTo: VesselSide.TBD
        }),
        client = ajax.Url<BerthReservation, DataCacheUpdateEntities>(entrypoints.AddShift, {
            useCache: true,
            include: [br => br.Itinerary]
        }),
        berths = lift(() => Array.from(data.Berths.items()))
            .filter(b =>
                !!b.Location() &&
                !!previousPoint() &&
                !!previousPoint()!.Location() &&
                b.Location()!.Port() === previousPoint()!.Location()!.Port()
            ),
        berthQuery = S.data(''),
        berthTypeahead = new Typeahead<Berth>({
            typeaheadOptions: {
                minLength: 0,
                highlight: true,
                hint: false
            },
            collection: berths,
            observable: berthQuery,
            matchFn: (berth, query) => !!berth.Location() &&
                CaseInsensitiveContains(berth.Location()!.Name(), query),
            displayKey: berth => berth.Location() && berth.Location()!.Name() || '',
            onSelect: berth => newPoint.LocationId(berth.Id())
        }),
        submitting = S.data(false),
        Validation = ValidationWatcher(client),
        AgentRestrictions = S(() => agentRestrictionErrors(Validation())),
        ShowAgentRestrictions = S.value(false);
        
    S(() => ShowAgentRestrictions(Object.keys(AgentRestrictions()).length > 0));

    data.ItineraryPoints.attach(newPoint, [newPoint.Location]);
    newReservation.Itinerary(newPoint);

    S(() => previousPoint() && newPoint.PortCallId(previousPoint()!.PortCallId()));

    S(() => {
        const berth = newPoint.Location() && newPoint.Location()!.Berth();
        if (!berth) return;

        newReservation.Footmark.UOM(berth.StartMark.UOM());

        const prev = previousPoint(),
            prevBerth = prev && prev.Location() && prev.Location()!.Berth();

        if (prev && prevBerth) {
            const
                // TODO: It'd be slick to figure out if this "shift" is really the in (or out) movement from
                // (or to) an anchorage, in which case using the arrival (or departure) berth's
                // BerthTransitOffset would probably make more sense here.
                transitMins = prevBerth.PierGroup() === berth.PierGroup() ? SAME_PIERGROUP_TRANSIT_MINS : DIFFERENT_PIERGROUP_TRANSIT_MINS,
                arrival = tzmomentMod(tzutils, prev.Departure.Effective(), m => m.add(transitMins, 'minutes')),
                departure = tzmomentMod(tzutils, arrival, m => m.add(DEFAULT_NEW_SHIFT_DURATION_MINS, 'minutes'));
            newPoint.Arrival.Estimated(arrival);
            newPoint.Departure.Estimated(departure);
        }
    });

    return {
        BerthTypeahead: berthTypeahead,
        CanSubmit: () =>
            // TH 02/13/2017 -- Make sure we've populated all the fields we care about.
            // Location is the main one, but the others could start out unpopulated if somehow
            // the previous point wasn't fully populated.
            !!newPoint.Location() &&
            !!newReservation.Footmark.UOM() &&
            !!newPoint.PortCallId() &&
            +newPoint.Arrival.Estimated() > 0 &&
            +newPoint.Departure.Estimated() > 0,
        Cancel: onCancel,
        Submit,
        Submitting: submitting,
        AgentRestrictions,
        ShowAgentRestrictions
    };

    function Submit() {
        if (submitting()) return;
        submitting(true);
        client.Post(newReservation).then(onSuccess).finally(() => submitting(false));
    }
}
export type AddShiftPopup = ReturnType<typeof AddShiftPopup>;


// WEBPACK FOOTER //
// ./apps/pilots/addshiftpopup/model.ts